import Button from '../shared/Button.js';
import Segmented from '../shared/Segmented.js';
// Auto-extracted by Phase 2 Round 2 (verbatim). See MIGRATION_PLAN §6.
import React from 'react';
import {getProgramColor} from '../lib/programColors.js';

import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions, dailySeqItems} from '../lib/recordSequence.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {buildPigDailyExportColumns} from '../lib/dailyReportExports.js';
import {printRows} from '../lib/printExport.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
import type {SavedViewState} from '../lib/savedViewsApi.js';
// Loaded on the first Add Report click (LazyAdminAddReportModal), not at startup.
import AdminAddReportModal from '../shared/LazyAdminAddReportModal.tsx';
import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';

import InlineNotice from '../shared/InlineNotice.jsx';

import {DailyCardList, feedLbsVal, mutedVal, voltageVal, check, commentText} from '../shared/DailyRecordCards.jsx';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
const PigDailyPage = React.lazy(() => import('./PigDailyPage.tsx'));
import {loadAllPigDailys} from './pigDailysLoader.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {PigDailysGroupDirectoryData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {usePaginatedResource} from '../app/data/usePaginatedResource.js';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {matchesTeamMemberFilter, teamMemberOptions, teamMemberSelectValue} from '../lib/teamMemberFilter.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useDailysRecent} from '../contexts/DailysRecentContext.jsx';
import Header from '../shared/Header.jsx';
import type {Notice} from '../shared/InlineNotice.js';

const PIG_DAILYS_SURFACE_KEY = 'pig.dailys';
// Frozen module constant, not an inline object literal: useRouteResource
// serializes params into its key, and a fresh {} every render would rebuild the
// key and re-run everything downstream of it forever.
const PIG_DAILYS_DIRECTORY_PARAMS = Object.freeze({});
const VALID_PIG_DAILY_SOURCE_FILTERS = new Set(['all', 'daily', 'addfeed']);

// ── Props ───────────────────────────────────────────────────────────────────
// NOT props from PigEntry any more. PigEntry mounts <PigDailysView /> with
// nothing at all, and all three of the props it used to thread are gone for
// three different reasons:
//
//   feederGroups   was App's former-eager ppp-feeders-v1 boot array. The router
//                  reads it itself now, through pig.dailys.groupDirectory, so
//                  `pigdailys` retires from the frozen app_store consumer list
//                  in tests/fixtures/former-eager-dependencies.json. It is
//                  still handed from the ROUTER to the two surfaces below,
//                  which is a different thing entirely: the provenance
//                  derivation scores a hand-off in the DOMAIN ENTRY, because
//                  that is where a boot value enters the route.
//   setPigDailys   is App's `useDailysRecent().setPigDailys`, reached through a
//                  prop for no reason but history. The hub calls the same
//                  setter off the same provider directly. A write is not a
//                  read: the derivation scores setters as non-consuming.
//   refreshDailys  was App's global post-write fan-out. Its pig branch is
//                  `loadAllPigDailys(sb)` into that same setter, and the hub
//                  owns that call directly now — see refreshPigDailysRecent.
type RowList = unknown[];

// ── Row shapes ──────────────────────────────────────────────────────────────

// A pig_dailys row, reduced to the columns this hub reads. The select is `*`
// and the client carries no generated database types, so `data` arrives as
// `any`; this records what the hub actually depends on rather than mirroring
// SQL. Sibling shape: src/pig/PigDailyPage.tsx's DailyRecord, over the same
// table. The numeric columns stay `number | string` because the column may
// arrive typed or as text.
//
// `date` is not optional: the date-range filter compares it relationally and
// the record page's delete label concatenates it, and every writer (the record
// page, the public webform, the admin add-report modal) supplies one.
type PigDailyRecord = {
  id?: string;
  date: string;
  team_member?: string | null;
  batch_label?: string | null;
  batch_id?: string | null;
  pig_count?: number | string | null;
  feed_lbs?: number | string | null;
  group_moved?: boolean | null;
  nipple_drinker_moved?: boolean | null;
  nipple_drinker_working?: boolean | null;
  troughs_moved?: boolean | null;
  fence_walked?: boolean | null;
  fence_voltage?: number | string | null;
  issues?: string | null;
  source?: string | null;
  // Only ever probed with Array.isArray before being handed to
  // DailyPhotoThumbnails, so `unknown` is what is known.
  photos?: readonly unknown[] | null;
};

// A ppp-feeders-v1 row, reduced to the three fields the group dropdown and the
// batch_id derivation read.
type PigDailyFeederGroup = {
  id?: string | null;
  batchName?: string | null;
  subBatches?: {id?: string | null; name?: string | null}[] | null;
};

// An app_saved_views row, reduced to the columns this hub reads.
// src/lib/savedViewsApi.ts returns these rows as `unknown` on purpose — it never
// touches the columns it selects — so the five below are named against that
// module's own select list.
type SavedViewRow = {
  id: string;
  name?: string | null;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};

// The saved filter payload, exactly as pigDailysViewState() below writes it —
// that function is this surface's only writer, and applyPigDailysSavedView is
// its only reader. Every field is `string` for that reason; the
// `typeof … === 'string'` tests in the reader are the runtime check for a row
// written before a field existed, and they are unchanged.
type PigDailysViewState = {fBatch: string; fTeam: string; fFrom: string; fTo: string; srcFilter: string};

// The `e.message` half of the `e.message || …` fallbacks below. A caught value
// is `unknown` under strict mode, and `instanceof Error` is the wrong probe:
// supabase-js rejects with plain PostgrestError objects that carry `message`
// without being Errors. Returning '' for a value with no usable message leaves
// the existing `||` fallbacks behaving exactly as before.
function errMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const message = (e as {message?: unknown}).message;
    if (message) return String(message);
  }
  return '';
}

function PigDailysRouter() {
  const {authState} = useAuth();
  const location = useLocation();
  // The feeder directory is read HERE, above the index/record branch, because
  // both surfaces need it and neither owns it alone — and because this is
  // exactly where the PigEntry prop hand-off used to land, which is what makes
  // pig.dailys.groupDirectory a replacement rather than an addition.
  const directoryResource = useRouteResource(RESOURCE.PIG_DAILYS_GROUP_DIRECTORY, PIG_DAILYS_DIRECTORY_PARAMS);
  const directory = directoryResource.data as PigDailysGroupDirectoryData | null;
  // The one place this file states what it believes the opaque blob holds. The
  // resource payload is `unknown[]` exactly as PigContext handed it out, so the
  // assertion sits at the single point where the payload and its two consumers'
  // contract meet, and the reads below stay unannotated.
  const feederGroups = (directory?.feederGroups || []) as PigDailyFeederGroup[];
  const dailyId = location.pathname.startsWith('/pig/dailys/')
    ? location.pathname.slice('/pig/dailys/'.length) || null
    : null;
  if (dailyId) {
    return React.createElement(
      React.Suspense,
      {
        fallback: React.createElement(
          'div',
          {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
          'Loading Pig Daily Record…',
        ),
      },
      React.createElement(PigDailyPage, {
        sb,
        fmt,
        authState,
        feederGroups,
      }),
    );
  }
  return React.createElement(PigDailysHub);
}

const PigDailysHub = () => {
  const {authState} = useAuth();
  // The shared recent-dailys projection Home, Pigs Home, Sows and the pig batch
  // hub render from. It reached this component as a prop off App; it is the
  // same setter off the same provider, read directly.
  const {setPigDailys} = useDailysRecent();
  const {useState, useEffect} = React;
  const [fBatch, setFBatch] = usePersistentViewState('pig.dailys.batchFilter', '');
  const [fTeam, setFTeam] = usePersistentViewState('pig.dailys.teamFilter', '');
  const [fFrom, setFFrom] = usePersistentViewState('pig.dailys.fromFilter', '');
  const [fTo, setFTo] = usePersistentViewState('pig.dailys.toFilter', '');
  const [srcFilter, setSrcFilter] = usePersistentViewState('pig.dailys.sourceFilter', 'all');
  // Rows the admin Add Report modal just created, projected over the server
  // pages so they show before the next read. (The edit override and delete hide
  // projections went with the unreachable edit modal, E12a.)
  const [createdRecords, setCreatedRecords] = useState<PigDailyRecord[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [exportNotice, setExportNotice] = useState('');
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  // `authState?.user` cannot be used: AuthState includes the literal `false`,
  // which optional chaining does not remove; a truthiness test does.
  const myProfileId = (authState ? authState.user?.id : null) || null;
  const savedViewsQuery = useSavedViewsData(PIG_DAILYS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;

  // The list's rows, its cursor, its cancellation and its stale-key protection
  // are the paginated resource's now. `reload` is in the page key on purpose:
  // Retry must start a NEW complete sequence rather than same-key refetch an
  // already-partial list — the same contract cattle.dailys.list documents.
  const dailysParams = React.useMemo(() => ({reload: reloadKey}), [reloadKey]);
  const dailysResource = usePaginatedResource<PigDailyRecord, number>(RESOURCE.PIG_DAILYS_LIST, dailysParams);
  const {
    error: listError,
    hasMore: listHasMore,
    loadMore,
    loadMoreError: listLoadMoreError,
    loadingMore: listLoadingMore,
  } = dailysResource;
  const loading = dailysResource.loading;
  const loadError = React.useMemo<Notice | null>(
    () =>
      listError
        ? {
            kind: 'error',
            message:
              'Could not load daily reports. Please refresh the page. (' +
              (errMessage(listError) || String(listError)) +
              ')',
          }
        : listLoadMoreError
          ? {
              kind: 'error',
              message:
                'Could not load more daily reports. (' +
                (errMessage(listLoadMoreError) || String(listLoadMoreError)) +
                ')',
            }
          : null,
    [listError, listLoadMoreError],
  );

  // The server pages, with this session's local mutations projected over them.
  // Order is what the old single array produced: newly created rows first, then
  // the pages in server order, each row replaced by its override and skipped if
  // hidden. De-duplicated by id because the old `while` loop appended whole
  // pages into one array and the modal's onSaved unshifted into it.
  const records = React.useMemo(() => {
    const seen = new Set<PigDailyRecord['id']>();
    const rows: PigDailyRecord[] = [];
    for (const row of [...createdRecords, ...dailysResource.rows]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
    return rows;
  }, [createdRecords, dailysResource.rows]);

  // A Retry is a new server sequence, so its rows are authoritative again and
  // no transient local projection may leak into the new key.
  useEffect(() => {
    setCreatedRecords([]);
  }, [reloadKey]);

  // The eager "load every page" behaviour is unchanged; only the cursor moved.
  // `listLoadMoreError` is in the guard so a failed append stops the loop
  // instead of retrying it forever — the old loop threw out of its own `await`
  // and stopped, which had the same effect by a different mechanism.
  useEffect(() => {
    if (listHasMore && !listLoadingMore && !listLoadMoreError) {
      void loadMore();
    }
  }, [listHasMore, listLoadingMore, listLoadMoreError, loadMore]);

  // THE SHARED PROJECTION, PUBLISHED FROM THE SAME ROWS THIS HUB RENDERS.
  //
  // The old load effect published `all` into DailysRecentContext the moment its
  // page loop finished, and every mutation republished the mutated array. Both
  // are this one effect now: it waits for a COMPLETE sequence (a partial page
  // set was never published before and must not start being) and then publishes
  // the projected rows — which is exactly what the mutation paths used to send,
  // because they applied the same updater to `records` and to the context.
  // A failed list read publishes [], as the old `.catch` did.
  //
  // Publish only when the rows actually change. usePaginatedResource rebuilds
  // `rows` on every render, so `records` is a new array each time; publishing on
  // identity alone re-rendered App through DailysRecentContext, which rebuilt
  // `records` and published again — an endless loop that starved the router, so
  // opening a record changed the URL but never rendered PigDailyPage.
  const publishedRef = React.useRef<readonly unknown[] | null>(null);
  useEffect(() => {
    if (!listError && (loading || listHasMore || listLoadingMore)) return;
    const next: readonly unknown[] = listError ? [] : records;
    const prev = publishedRef.current;
    if (prev && prev.length === next.length && prev.every((row, i) => row === next[i])) return;
    publishedRef.current = next;
    setPigDailys(next as RowList);
  }, [records, loading, listHasMore, listLoadingMore, listError, setPigDailys]);

  // The pig branch of App's retired `refreshDailys` prop, owned here. It is the
  // same call App made — the fail-whole loader, then the same setter, and a
  // console.warn that leaves the last good context value in place — so a write
  // still reconciles the shared projection against the SERVER rather than only
  // against this hub's optimistic copy.
  const refreshPigDailysRecent = React.useCallback(() => {
    loadAllPigDailys(sb)
      .then((all) => setPigDailys(all))
      .catch((e: unknown) => {
        console.warn('pig_dailys refresh error:', errMessage(e) || e);
      });
  }, [setPigDailys]);

  // The three `(n): n is string` annotations below restate the SAME truthiness
  // test .filter(Boolean) already ran — Boolean ignores filter's index/array
  // arguments, so the surviving elements are identical. They are here because
  // the survivors feed <option value> and React keys, which reject null.
  const fromRecords = [...new Set(records.map((d) => d.batch_label).filter((n): n is string => Boolean(n)))].sort();

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  const navigate = useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);

  // Same annotated truthiness test as fromRecords/groupList above.
  const teamOpts = teamMemberOptions(records.map((r) => r.team_member));
  let filtered = records.filter(
    (r) =>
      (!fBatch || r.batch_label === fBatch) &&
      matchesTeamMemberFilter(r.team_member, fTeam) &&
      (!fFrom || r.date >= fFrom) &&
      (!fTo || r.date <= fTo) &&
      (srcFilter === 'all' ||
        (srcFilter === 'daily' && r.source !== 'add_feed_webform') ||
        (srcFilter === 'addfeed' && r.source === 'add_feed_webform')),
  );
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);

  // REAL DIVERGENCE, surfaced rather than fixed — see the report.
  // src/lib/savedViewsApi.ts declares `viewState` as SavedViewState
  // ({filters, sortRules, viewMode}) — the shape buildViewState() produces and
  // the cattle/sheep/equipment surfaces send. This surface has always stored a
  // FLAT filter object instead, and applyPigDailysSavedView below reads that
  // same flat shape back, so the round-trip works and the column is opaque
  // jsonb to the database. The assertion is placed here, once, on the return of
  // the function that owns the payload; reshaping the payload instead would
  // orphan every saved pig-dailys view already in the table. It erases at build
  // time, so both writers send exactly what they sent before.
  function pigDailysViewState(): SavedViewState {
    const state: PigDailysViewState = {
      fBatch: fBatch || '',
      fTeam: fTeam || '',
      fFrom: fFrom || '',
      fTo: fTo || '',
      srcFilter: VALID_PIG_DAILY_SOURCE_FILTERS.has(srcFilter) ? srcFilter : 'all',
    };
    return state as unknown as SavedViewState;
  }

  function applyPigDailysSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    // The other half of the divergence above: `view_state` is `unknown` because
    // only the surface that wrote it knows its keys. This is that surface, and
    // PigDailysViewState is exactly what pigDailysViewState() writes. The five
    // runtime tests below are unchanged and still cover a row saved before a
    // field existed.
    const st = (view.view_state || {}) as PigDailysViewState;
    setFBatch(typeof st.fBatch === 'string' ? st.fBatch : '');
    setFTeam(typeof st.fTeam === 'string' ? st.fTeam : '');
    setFFrom(typeof st.fFrom === 'string' ? st.fFrom : '');
    setFTo(typeof st.fTo === 'string' ? st.fTo : '');
    setSrcFilter(VALID_PIG_DAILY_SOURCE_FILTERS.has(st.srcFilter) ? st.srcFilter : 'all');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applyPigDailysSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      // createSavedView resolves with `unknown` for the same reason
      // the shared picker owner does. `id` is the one column this branch reads.
      const created = (await createSavedView(sb, {
        surfaceKey: PIG_DAILYS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: pigDailysViewState(),
      })) as {id?: string} | null;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: pigDailysViewState()});
      await loadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filters.',
      });
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + (errMessage(e) || String(e))});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  function handleExportCsv() {
    const columns = buildPigDailyExportColumns();
    const ok = downloadCsv(csvFilename('pig-dailys'), rowsToCsv(columns, filtered));
    setExportNotice(ok ? '' : 'CSV export is only available in the browser.');
  }

  function handlePrintRows() {
    const columns = buildPigDailyExportColumns();
    const ok = printRows({
      title: 'Pig Dailys',
      subtitle: filtered.length + ' filtered daily reports',
      columns,
      rows: filtered,
    });
    setExportNotice(ok ? '' : 'Print is only available in the browser.');
  }

  // String() around a value that is already numeric: parseFloat is DECLARED to take a string and did the same ToString conversion itself.
  const totalFeed = filtered.reduce((s, r) => s + (parseFloat(String(r.feed_lbs)) || 0), 0);
  const fi = {
    padding: '6px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    fontSize: 12,
    fontFamily: 'inherit',
    background: 'var(--surface)',
    width: 'auto',
  };
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  const savedViewGhostBtnS = {
    padding: '6px 12px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    background: 'var(--surface)',
    color: 'var(--ink)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };

  const savedViewRadioLabelS = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--ink)',
    cursor: 'pointer',
  };

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-pig-dailys-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      <div style={{padding: '1rem', maxWidth: 1100, margin: '0 auto'}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 style={{fontSize: 30, fontWeight: 800, color: 'var(--ink)', margin: 0}}>Pig Dailys</h1>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
              {records.length.toLocaleString()} total
            </div>
          </div>
          <div style={{display: 'flex', gap: 8}}>
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              + Add Report
            </Button>
          </div>
        </div>
        {showAddModal && (
          <AdminAddReportModal
            sb={sb}
            formType="pig"
            onClose={() => setShowAddModal(false)}
            // AdminAddReportModal is generic over the row type it hands back —
            // it inserts through a client with no generated database types, so
            // the shape is the caller's to name. This annotation is what binds
            // that type parameter: the modal calls onSaved with the inserted
            // pig_dailys rows (`onSaved(data)` / `onSaved([data.row])`).
            onSaved={(recs: PigDailyRecord[]) => {
              setCreatedRecords((p) => [...recs, ...p]);
              refreshPigDailysRecent();
            }}
          />
        )}
        {!loadError && (
          <>
            <div
              data-pig-dailys-saved-views-row
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}} data-pig-dailys-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-pig-dailys-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...fi, minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        type="button"
                        data-pig-dailys-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-pig-dailys-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span
                      style={{
                        fontSize: 12,
                        color: savedViewNotice.kind === 'success' ? 'var(--ok-ink)' : 'var(--danger-ink)',
                      }}
                    >
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    type="button"
                    data-pig-dailys-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-pig-dailys-saved-view-form
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-pig-dailys-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...fi, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="savePigDailysViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-pig-dailys-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="savePigDailysViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-pig-dailys-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button type="button" data-pig-dailys-saved-view-save onClick={submitSaveView} disabled={savedViewBusy}>
                  Save
                </Button>
                <Button type="button" onClick={() => setShowSaveViewForm(false)} disabled={savedViewBusy}>
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
        {exportNotice && <div style={{marginBottom: 14, color: 'var(--danger-ink)', fontSize: 12}}>{exportNotice}</div>}
        <div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center'}}>
          <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={{...fi, width: 130}} />
          <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>to</span>
          <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={{...fi, width: 130}} />
          <select value={fBatch} onChange={(e) => setFBatch(e.target.value)} style={fi}>
            <option value="">All groups</option>
            {fromRecords.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          <select
            value={teamMemberSelectValue(fTeam, teamOpts)}
            onChange={(e) => setFTeam(e.target.value)}
            style={fi}
            data-pig-dailys-team-filter="1"
          >
            <option value="">All team members</option>
            {teamOpts.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {(fBatch || fTeam || fFrom || fTo || srcFilter !== 'all') && (
            <Button
              onClick={() => {
                setFBatch('');
                setFTeam('');
                setFFrom('');
                setFTo('');
                setSrcFilter('all');
              }}
            >
              Clear
            </Button>
          )}
          <Segmented
            aria-label="Report Source"
            value={srcFilter}
            onChange={setSrcFilter}
            options={[
              {value: 'all', label: 'All'},
              {value: 'daily', label: 'Daily Reports'},
              {value: 'addfeed', label: 'Add Feed'},
            ]}
            style={{marginLeft: 'auto'}}
          />
          <Button
            type="button"
            data-pig-dailys-export-csv="1"
            onClick={handleExportCsv}
            disabled={loading || !!loadError}
          >
            Export CSV
          </Button>
          <Button type="button" data-pig-dailys-print="1" onClick={handlePrintRows} disabled={loading || !!loadError}>
            Print
          </Button>
        </div>
        <InlineNotice notice={loadError} />
        {loadError && (
          <Button
            type="button"
            data-daily-list-retry="1"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{marginBottom: 12}}
          >
            Retry
          </Button>
        )}
        {loading && <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading...</div>}
        <OperationalListEmptyState
          loading={loading}
          loadError={loadError}
          totalCount={records.length}
          filteredCount={filtered.length}
          emptyLabel="No pig daily reports yet"
        />
        {!loading && !loadError && filtered.length > 0 && (
          <DailyCardList
            program="pig"
            // DailyCardList is still JavaScript, and its `rows = []` default is all
            // TypeScript has to infer that prop from, so it reads as `never[]` — a
            // description of the DEFAULT VALUE, not of the component, which reads
            // d.date / d.id off every row. The assertion records that mismatch at
            // the one boundary where it bites; it comes out when DailyRecordCards
            // itself converts.
            rows={filtered as unknown as never[]}
            fmt={fmt}
            maxInitialRows={100}
            // DailyCardList is still JavaScript, so its three row callbacks have
            // no contextual type; each is called with one row of `rows`, which
            // is `filtered` directly above.
            onOpen={(row) => {
              const d = row as PigDailyRecord;
              navigate('/pig/dailys/' + d.id, recordSeqNavOptions(dailySeqItems(filtered, 'batch_label')));
            }}
            rowAttrs={(row) => {
              const d = row as PigDailyRecord;
              return {'data-daily-row': d.id};
            }}
            mapRow={(row) => {
              const d = row as PigDailyRecord;
              return {
                name: d.batch_label || '—',
                team: d.team_member || '—',
                source: d.source,
                photos: d.photos,
                vals: {
                  feed: feedLbsVal(d.feed_lbs),
                  // String() around a value that is already numeric: parseInt is DECLARED to take a string and did the same ToString conversion itself.
                  pigs: parseInt(String(d.pig_count)) > 0 ? d.pig_count + ' pigs' : mutedVal('—'),
                  volt: voltageVal(d.fence_voltage),
                },
                checks: [
                  check('Moved', d.group_moved !== false),
                  check('Nipple', d.nipple_drinker_working !== false),
                  check('Fence', d.fence_walked !== false),
                ],
                comment: commentText(d.issues),
              };
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PigDailysRouter;
